import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";

// Falls back to an initial-letter avatar when there's no profile picture, or
// the picture fails to load (broken/expired CDN link).
const AccountAvatar = ({ name = "", profilePictureUrl = "" }) => {
  const [imgError, setImgError] = useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase();

  if (profilePictureUrl && !imgError) {
    return (
      <img
        className="liAccountAvatar"
        src={profilePictureUrl}
        onError={() => setImgError(true)}
        alt={name}
      />
    );
  }
  return (
    <span className="liAccountAvatar liAccountAvatarFallback">{initial}</span>
  );
};

const AccountRow = ({ account, active = false, onClick = () => {} }) => (
  <>
    <AccountAvatar
      name={account?.name}
      profilePictureUrl={account?.profilePictureUrl}
    />
    <span className="liAccountSelectName">{account?.name}</span>
    {account?.isSalesNavigator && (
      <span className="statsBadge activeBadge">Sales Navigator</span>
    )}
    {active && <i className="material-icons checkIcon">check</i>}
  </>
);

// Single-select LinkedIn account picker: a closed trigger showing the current
// pick (avatar + name + Sales Navigator badge) that opens a short list of
// the same rows - not a searchable text input, since there are only ever a
// handful of connected accounts to choose from.
const LiAccountSelectDropdown = observer(props => {
  const {
    optionList = [],
    value = "",
    cbk = () => {},
    disabled = false
  } = props || {};

  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  const selected = optionList.find(item => item?.id === value) || null;

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = item => {
    cbk(item);
    setShowDropdown(false);
  };

  return (
    <div
      className={`liAccountSelectDropdown ${disabled ? "disabled" : ""}`}
      ref={wrapperRef}
    >
      <div
        className={`liAccountSelectTrigger ${showDropdown ? "open" : ""} ${
          !selected ? "placeholder" : ""
        }`}
        onClick={() => !disabled && setShowDropdown(!showDropdown)}
      >
        {selected ? (
          <AccountRow account={selected} />
        ) : (
          <span className="liAccountSelectPlaceholder">
            Select a LinkedIn account
          </span>
        )}
        <i
          className={`material-icons arrowIcon ${
            showDropdown ? "rotate" : ""
          }`}
        >
          keyboard_arrow_down
        </i>
      </div>
      {showDropdown && (
        <ul className="liAccountSelectPanel">
          {optionList.map(item => (
            <li
              key={item.id}
              className={`liAccountSelectOption ${
                item.id === value ? "active" : ""
              }`}
              onClick={() => handleSelect(item)}
            >
              <AccountRow account={item} active={item.id === value} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export { LiAccountSelectDropdown };
export default LiAccountSelectDropdown;
